import styled from 'styled-components';
import { useIsMobile } from '../hooks/use-is-mobile';
import {
  breakpoints,
  colors,
  Divider,
  DividerDiamond,
  fonts,
  FramedImage,
  Page,
  PullQuote,
  SectionTitle,
} from '../styles/theme';
import img2 from './images/enn530783.png';
import img1 from './images/fcb-profill.png';
import img3 from './images/olafurkram.png';
import { MobileAbout } from './mobileView';

const HeroSection = styled.section`
  display: grid;
  grid-template-columns: minmax(280px, 380px) 1fr;
  gap: 64px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
    max-width: 640px;
    margin: 0 auto;
  }
`;

const HeroImage = styled.div`
  position: sticky;
  top: 110px;

  @media (max-width: 900px) {
    position: static;
    max-width: 340px;
    margin: 0 auto;
    width: 100%;
  }
`;

const BioText = styled.div`
  font-size: 16.5px;
  line-height: 1.85;
  color: ${colors.ink};
  text-align: justify;

  p + p,
  p + blockquote,
  blockquote + p {
    margin-top: 22px;
  }
`;

const BioLead = styled.p``;

const SpanName = styled.span`
  font-variant: all-small-caps;
  font-size: 1.3em;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${colors.gold};
`;

const SectionDivider = styled.div`
  margin: 84px 0;

  @media (max-width: ${breakpoints.mobile}) {
    margin: 56px 0;
  }
`;

const BookSection = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);
  gap: 56px;
  align-items: stretch;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    max-width: 640px;
    margin: 0 auto;
  }
`;

const BookInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  font-size: 16px;
  line-height: 1.8;
  text-align: justify;
`;

const AwardLine = styled.p<{ $accent?: boolean }>`
  font-weight: ${(p) => (p.$accent ? 600 : 500)};
  color: ${(p) => (p.$accent ? colors.accent : colors.ink)};
  letter-spacing: 0.02em;
`;

const JuryLabel = styled.p`
  font-size: 13px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${colors.inkSoft};
  margin-bottom: -8px;
`;

const QuotesPanel = styled.div`
  background: linear-gradient(150deg, ${colors.accent} 0%, ${colors.accentDeep} 100%);
  color: ${colors.goldLight};
  padding: 48px 44px;
  display: flex;
  gap: 40px;
  align-items: center;
  box-shadow: 0 24px 50px rgba(42, 30, 21, 0.25);

  @media (max-width: 1100px) {
    flex-direction: column;
    padding: 40px 32px;
  }
`;

const PanelImage = styled.img`
  width: 190px;
  flex: none;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.4);
`;

const QuoteList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: center;

  @media (max-width: 1100px) {
    text-align: center;
  }
`;

const Quote = styled.div`
  font-family: ${fonts.display};
  font-style: italic;
  font-size: 18px;
  line-height: 1.55;
`;

const QuoteSource = styled.div`
  font-family: ${fonts.body};
  font-style: normal;
  font-size: 12.5px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${colors.gold};
  margin-top: 6px;
`;

const Stars = styled.div`
  color: ${colors.gold};
  font-size: 18px;
  letter-spacing: 0.35em;
`;

const BandSection = styled.section`
  background: ${colors.cream};
  border: 1px solid ${colors.line};
  padding: 56px;
  display: grid;
  grid-template-columns: minmax(220px, 300px) 1fr;
  gap: 56px;
  align-items: center;

  @media (max-width: 940px) {
    grid-template-columns: 1fr;
    padding: 40px 28px;
    gap: 36px;
    max-width: 640px;
    margin: 0 auto;
  }
`;

const BandText = styled.div`
  font-size: 16px;
  line-height: 1.8;
  text-align: justify;

  h2 {
    margin-bottom: 18px;
  }
`;

export function About2() {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <MobileAbout />;
  }

  return (
    <Page>
      <HeroSection>
        <HeroImage>
          <FramedImage>
            <img src={img1} alt="Birgitta Björg Guðmarsdóttir" />
          </FramedImage>
        </HeroImage>
        <BioText>
          <BioLead>
            <SpanName>Birgitta Björg</SpanName> Guðmarsdóttir (f. 1998) er skáld og tónlistarkona, fædd og uppalin í Reykjavík.
          </BioLead>
          <p>
            Hún gaf út sína fyrstu skáldsögu Skotheld (2018) aðeins tvítug að aldri en önnur skáldsaga hennar, Moldin heit (2024) hlaut Fjöruverðlaunin 2025 í flokki fagurbókmennta. Um verkið hafði gagnrýnandi Heimildarinnar þetta að segja:
          </p>
          <PullQuote>
            „Moldin heit er verulega fín og kröftug skáldsaga og ég vona að höfundurinn Birgitta Björg verði fastagestur í bókahillum landsmanna um ókomin ár.”
          </PullQuote>
          <p>
          <p>
            Birgitta hefur síðan gefið út ljóðabókina Draugamandarínur (2025) sem hlaut Bóksalaverðlaunin í flokki ljóðabóka.
          </p>
            Birgitta er stofnmeðlimur hljómsveitarinnar Ólafs Kram sem sigraði Músíktilraunir 2021 og hefur síðan gefið út tvær plötur, nefrennsli / kossaflens (2021) og Ekki treysta fiskunum (2022).
          </p>
          <p>
            Verk Birgittu hafa birst í Tímariti Máls og Menningar, Skírni og í Stelk. Hún er meðlimur í listakollektívunni Heysahorn sem gefur út listatímaritið LYNG. Þá er Birgitta meðlimur í ljóðagenginu MÚKK.
          </p>
        </BioText>
      </HeroSection>

      <SectionDivider>
        <Divider>
          <DividerDiamond />
        </Divider>
      </SectionDivider>

      <BookSection>
        <BookInfo>
          <SectionTitle>Moldin heit</SectionTitle>
          <p>
            Gefin út af Drápu bókaútgáfu, fáanleg í helstu bókabúðum landsins.
          </p>
          <AwardLine>Tilnefnd til Íslensku bókmenntaverðlaunanna 2025.</AwardLine>
          <AwardLine $accent>Hlaut Fjöruverðlaunin 2025.</AwardLine>
          <JuryLabel>Rökstuðningur dómnefndar:</JuryLabel>
          <p>
            Listir, náttúra og ólgandi tilfinningar takast á í bókinni Moldin heit eftir Birgittu Björgu Guðmarsdóttur.
            Hrynjandi ljóðræns textans knýr framvindu áfram þar sem meginstef eru ást, missir og innri leit. Form texta og litanotkun ljær frásögninni dýpt og er lesanda gefið rými til að
            skynja og túlka atburðarás sem lýkst upp eftir því sem líður á og rís hæst í sólódansi aðalpersónunnar. Birgitta Björg slær hér nýjan og forvitnilegan bókmenntatón.
          </p>
        </BookInfo>
        <QuotesPanel>
          <PanelImage src={img2} alt="Moldin heit" />
          <QuoteList>
            <Quote>
              „…athugult og athyglisvert verk…”
              <QuoteSource>- Díana Sjöfn Jóhannsdóttir, Lestrarklefanum</QuoteSource>
            </Quote>
            <Quote>
              „…kröftug skáldsaga…“
              <QuoteSource>- Salka Guðmundsdóttir, Heimildinni</QuoteSource>
            </Quote>
            <Quote>
              „…algjörlega húðvönduð!“
              <QuoteSource>- Egill Helgason, Kiljunni</QuoteSource>
            </Quote>
            <Quote>
              <Stars>★ ★ ★ ★</Stars>
              <QuoteSource>- Ragnheiður BirgisdóttirMorgunblaðinu</QuoteSource>
            </Quote>
          </QuoteList>
        </QuotesPanel>
      </BookSection>

      <SectionDivider>
        <Divider>
          <DividerDiamond />
        </Divider>
      </SectionDivider>

      <BandSection>
        <FramedImage>
          <img src={img3} alt="Ólafur Kram" />
        </FramedImage>
        <BandText>
          <SectionTitle>ólafur kram</SectionTitle>
          <p>
            Ólafur Kram er hljómsveit frá höfuðborgarsvæðinu sem leggur áherslu á að vinna út frá mismunandi tónlistarstefnum og blanda þeim saman í eina heild. Þau spila furðulega tónlist sem erfitt er að skilgreina.
            Súrrealískir textar og alls konar tónlistaráhrif blandast saman í gómsæta hljóðaveislu.
            Dísætur söngur í bland við djöflaöskur og fíflagang. Ólafur Kram sigraði Músíktilraunir árið 2021 auk þess að fá viðurkenningu fyrir textasmíð.
            Hljómsveitin samanstendur af Birgittu Björg, Eydísi Kvaran, Guðnýju Margréti, Iðunni Gígju og Sævari Andra.
          </p>
        </BandText>
      </BandSection>
    </Page>
  );
}
